import React, { forwardRef, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import FormikErrorMessage from '../field/message';
import ErrorMessage from '../field-message/error-message';
import HelpMessage from '../field-message/help-message';
import { Label } from '../../styled/label';
import SimpleRichText from '../../rich-text/simple';

import { RichTextPropTypes } from '../../../prop-types';
import { parseBytes, parseError } from './helpers';
import { bytesInMb, maxTotalFileSize } from './constants';
import {
  Wrapper,
  Dropzone,
  ErrorsList,
  FilesList,
  RemoveButton,
  AsideWrapper,
  RequirementsList,
  ScanConsent,
} from './styles';

const DropZone = forwardRef(
  (
    {
      name,
      label,
      maxFileSize: maxFileSizeInMb,
      maxFilesAccepted,
      fileTypesAccepted,
      setFieldValue,
      value: { files: acceptedFiles, redaction },
      helpText,
      error: errorStatus,
      title,
      description,
      required,
    },
    ref
  ) => {
    const [totalFileSize, setTotalFileSize] = useState(0);
    const [errors, setErrors] = useState([]);
    const [rejectedFiles, setRejectedFiles] = useState([]);
    const maxFileSize = maxFileSizeInMb * bytesInMb;
    const minFileSize = 1000;

    const handleMaxFileError = useCallback(() => {
      const i = errors.findIndex((err) => err.code === 'file-number-exceeded');

      if (acceptedFiles.length > maxFilesAccepted) {
        if (i === -1) {
          setErrors([
            ...errors,
            {
              code: 'file-number-exceeded',
              message: `Total number of files exceeds limit: ${maxFilesAccepted} files`,
            },
          ]);
        }
      } else if (i !== -1) {
        errors.splice(i, 1);
      }
    }, [acceptedFiles, errors, maxFilesAccepted]);

    const handleTotalSizeError = useCallback(() => {
      const i = errors.findIndex((err) => err.code === 'total-size-exceeded');

      if (totalFileSize >= maxTotalFileSize) {
        if (i === -1) {
          setErrors([
            ...errors,
            {
              code: 'total-size-exceeded',
              message: `Total upload size exceeds limit: ${parseBytes(
                maxTotalFileSize
              )}`,
            },
          ]);
        }
      } else if (i !== -1) {
        errors.splice(i, 1);
      }
    }, [errors, totalFileSize]);

    const onDropAccepted = useCallback(
      (droppedFiles) => {
        if (!droppedFiles.length) {
          return;
        }
        const uniqueFiles = [...acceptedFiles];
        droppedFiles.forEach((file) => {
          if (!uniqueFiles.some((f) => f.path === file.path)) {
            file.filename = file.path;
            uniqueFiles.push(file);
          }
        });

        setFieldValue(name, { files: uniqueFiles, redaction });
      },
      [setFieldValue, acceptedFiles, name, redaction]
    );

    const onDropRejected = useCallback(
      (droppedFiles) => {
        if (!droppedFiles.length) {
          return;
        }

        const uniqueFiles = [...rejectedFiles];
        droppedFiles.forEach((reject) => {
          if (!uniqueFiles.some((file) => file.path === reject.file.path)) {
            uniqueFiles.push(reject);
          }
        });

        setRejectedFiles(uniqueFiles);
      },
      [rejectedFiles]
    );

    useEffect(() => {
      if (!acceptedFiles.length) {
        return;
      }
      setTotalFileSize(
        acceptedFiles.reduce((acc, current) => acc + current.size, 0)
      );
      handleMaxFileError();
    }, [acceptedFiles, handleMaxFileError]);

    useEffect(() => {
      handleTotalSizeError();
    }, [totalFileSize, handleTotalSizeError]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDropAccepted,
      onDropRejected,
      minSize: minFileSize,
      maxSize: maxFileSize,
      accept: fileTypesAccepted.toString(),
    });

    const handleRemoveFile = (filePath) => {
      setFieldValue(name, {
        files: acceptedFiles.filter((file) => file.path !== filePath),
        redaction,
      });
    };

    const handleDismissRejectedFile = (filePath) => {
      setRejectedFiles(
        rejectedFiles.filter((reject) => reject.file.path !== filePath)
      );
    };

    return (
      <Wrapper data-input-scroll-id={`${name}[files]`} ref={ref}>
        <h3>{title}</h3>
        {description && <SimpleRichText richText={description} />}
        <RequirementsList>
          {required && <li>This is a required field</li>}
          <li>You can upload up to {maxFilesAccepted} files</li>
          <li>Maximum file size: {parseBytes(maxFileSize)}</li>
          <li>
            Accepted file types:
            <ul>
              <li>
                {fileTypesAccepted.map((type, i) =>
                  fileTypesAccepted.length - 1 === i ? (
                    <span key={i}>{type}</span>
                  ) : (
                    <span key={i}>{type}, </span>
                  )
                )}
              </li>
            </ul>
          </li>
        </RequirementsList>

        <Dropzone
          $isDragActive={isDragActive}
          $errorStatus={errorStatus}
          {...getRootProps()}
        >
          <input id={`${name}`} name={`${name}`} {...getInputProps()} />
          {label && <Label htmlFor={`${name}`}>{label}</Label>}
          {helpText && <HelpMessage id={`${name}_help`} message={helpText} />}
        </Dropzone>

        <AsideWrapper>
          {acceptedFiles.length ? (
            <FilesList data-testid="accepted-files">
              {acceptedFiles.map((file) => (
                <li id={`${name}[${file.path}]`} key={file.path}>
                  {file.path} ({parseBytes(file.size)})
                  <RemoveButton onClick={() => handleRemoveFile(file.path)} />
                </li>
              ))}
            </FilesList>
          ) : null}

          {rejectedFiles.length ? (
            <FilesList data-testid="rejected-files">
              {rejectedFiles.map(({ file, errors: fileErrors }) => (
                <li key={file.path}>
                  <div>
                    {file.path} ({parseBytes(file.size)})
                    <ul>
                      {fileErrors.map((err) => (
                        <li key={err.code}>
                          <ErrorMessage
                            id={`${name}[${file.path}]_error`}
                            message={parseError(
                              err.message,
                              minFileSize,
                              maxFileSize
                            )}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <RemoveButton
                    onClick={() => handleDismissRejectedFile(file.path)}
                    value="Dismiss"
                  />
                </li>
              ))}
            </FilesList>
          ) : null}

          {errors.length ? (
            <ErrorsList id="file-errors">
              {errors.map((err) => (
                <li key={err.code}>
                  <ErrorMessage
                    id={`${name}[${err.code}]_error`}
                    message={err.message}
                  />
                </li>
              ))}
            </ErrorsList>
          ) : null}

          <FormikErrorMessage id={`${name}_error`} name={`${name}[files]`} />

          <ScanConsent>
            We upload your files to a third party scanning service to scan for
            viruses and other malicious content. This service is entirely
            automated and they do not store a copy of the file. By uploading
            files using this form you consent to the submitting of the files to
            this service.
          </ScanConsent>
        </AsideWrapper>
      </Wrapper>
    );
  }
);

DropZone.displayName = 'DropZone';

DropZone.propTypes = {
  type: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  value: PropTypes.array,
  name: PropTypes.string,
  description: PropTypes.shape({
    text: RichTextPropTypes,
  }),
  fileTypesAccepted: PropTypes.arrayOf(PropTypes.string),
  helpText: PropTypes.string,
  label: PropTypes.string,
  maxFileSize: PropTypes.number,
  maxFilesAccepted: PropTypes.number,
  title: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  infoRedactionRequired: PropTypes.bool,
};

export default DropZone;
